import { Box, Divider, Typography } from '@mui/material';
import { useParams } from 'react-router';
import { useGetCatalogQuery, useGetPhaseQuery } from '../../api/content.api';
import { AiToolRow } from '../../components/AiToolRow';
import { ContentSection } from '../../components/ContentSection';
import { IcomCards } from '../../components/IcomCards';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { ProcessCard } from '../../components/ProcessCard';
import { ProseSections } from '../../components/ProseSections';
import { ViewInConfluence } from '../../components/ViewInConfluence';
import { SECTION_COPY, countsLine, methodsByProcess } from '../../content';
import { cardGridSx, sectionSpacingSx } from '../../theme';
import { ContentState } from '../../components/ContentState';

/**
 * `/sdlc/:phaseSlug` — one phase: what it is, why it exists, how it runs, and the
 * processes underneath it. **One segment**, because a phase's `path` is exactly
 * its slug.
 *
 * **There is no AI signature line**, which the designs draw in 24px stage colour
 * and no field produces. `aiToolList[0]` there is one arbitrarily-ordered tool
 * above the row that already names all of them.
 *
 * **It reads the catalog as well as the phase**, because
 * `GET /content/phases/{slug}` returns the processes but not their methods and
 * the process card lists the first few method names. One request per session
 * against twelve per-process ones.
 *
 * **A failure to load the catalog is silent.** The process cards lose their
 * method lists and keep their names, counts and links.
 */
export const PhasePage = () => {
  const { phaseSlug = '' } = useParams();
  const { data, isLoading, error, refetch } = useGetPhaseQuery(phaseSlug);
  const { data: catalog } = useGetCatalogQuery();

  const phase = data?.phase;
  const processes = data?.processes ?? [];
  const methods = methodsByProcess(catalog ?? []);
  const counts = phase === undefined ? undefined : countsLine(phase);

  return (
    <Box sx={{ paddingBottom: 8 }}>
      <Box sx={{ marginTop: 8 }}>
        <ContentState
          isLoading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          errorTitle="Unable to load this phase"
          errorNoun="phase"
          skeleton={<LoadingSkeleton layout="phase" label="Loading the phase" />}
        >
          {phase !== undefined && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 4,
                  }}
                >
                  <Typography
                    variant="overline"
                    component="p"
                    sx={{ color: 'text.secondary', letterSpacing: '1px' }}
                  >
                    Level 1 &middot; Phase {phase.phaseNumber}
                  </Typography>

                  <ViewInConfluence confluencePageId={phase.confluencePageId} />
                </Box>

                <Typography variant="h1" component="h1">
                  {phase.phase}
                </Typography>

                {counts !== undefined && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {counts}
                  </Typography>
                )}

                <AiToolRow tools={phase.content.aiToolList} />
              </Box>

              <Box sx={sectionSpacingSx}>
                <ProseSections content={phase.content} />
              </Box>

              <Box sx={sectionSpacingSx}>
                <IcomCards content={phase.content} />
              </Box>

              <Divider sx={sectionSpacingSx} />

              <Box sx={sectionSpacingSx}>
                <ContentSection
                  heading={SECTION_COPY.processes.heading}
                  caption={SECTION_COPY.processes.caption}
                >
                  {processes.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      No processes are catalogued under this phase yet.
                    </Typography>
                  ) : (
                    <Box component="ul" sx={{ ...cardGridSx('card'), listStyle: 'none', margin: 0, padding: 0 }}>
                      {processes.map((process) => (
                        <Box component="li" key={process.id} sx={{ display: 'flex' }}>
                          <ProcessCard
                            process={process}
                            reference={`${String(phase.phaseNumber)}.${String(process.number)}`}
                            methods={methods.get(process.path)}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </ContentSection>
              </Box>
            </>
          )}
        </ContentState>
      </Box>
    </Box>
  );
};
